import { useMemo } from "react";
import { TimesheetDay } from "domain/entities/timesheet-day";
import useLongPress from "presentation/mobile/hooks/useLongPress";
import MobileBadge from "presentation/mobile/components/MobileBadge";
import { abscenceTypesInDays } from "presentation/mobile/utils/abscence-types-in-days.util";
import { isVacationAbscence } from "presentation/mobile/utils/protected-abscence-types.util";

interface WeekDayCardProps {
    day: number;
    month: number;
    year: number;
    timesheetDay?: TimesheetDay;
    extraHourPoIds?: Set<string>;
    noHoursPoIds?: Set<string>;
    documentRequired?: boolean;
    hasDocument?: boolean;
    onDocumentClick?: (day: TimesheetDay) => void;
    isExpanded?: boolean;
    onToggleExpand?: () => void;
    onDeletePo?: (timesheetDay: TimesheetDay, poId: string) => void;
    onDeleteAbscence?: (timesheetDay: TimesheetDay) => void;
    canEdit?: boolean;
    isSuportService?: boolean;
}

const WEEK_DAYS_SHORT = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"];

// SUBCOMPONENTES REUTILIZÁVEIS (DRY)

// 1. Squircle da Data
const DateSquircle = ({
    day,
    weekday,
    isToday,
    isWeekend,
}: {
    day: string;
    weekday: string;
    isToday: boolean;
    isWeekend: boolean;
}) => (
    <div
        className="flex flex-column align-items-center justify-content-center flex-shrink-0"
        style={{
            width: "46px",
            height: "50px",
            borderRadius: "8px",
            backgroundColor: isToday ? "#E6F0FB" : isWeekend ? "#F1F5F9" : "#F8FAFC",
            border: `1px solid ${isToday ? "#BFDBFE" : "#E2E8F0"}`,
        }}
    >
        <span
            className="font-bold"
            style={{
                fontSize: "1.1rem",
                lineHeight: 1,
                color: isToday ? "#0084D1" : isWeekend ? "#94A3B8" : "#0F172A",
            }}
        >
            {day}
        </span>
        <span
            className="font-bold"
            style={{
                fontSize: "0.65rem",
                color: isToday ? "#0084D1" : "#64748B",
                marginTop: "3px",
            }}
        >
            {weekday}
        </span>
    </div>
);

// 2. Badge de Total de Horas
const HoursBadge = ({
    hours,
    isToday,
}: {
    hours: number;
    isToday: boolean;
}) => (
    <span
        className="font-bold flex align-items-center justify-content-center"
        style={{
            borderRadius: "6px",
            padding: "3px 8px",
            fontSize: "0.8rem",
            backgroundColor: isToday && hours > 0 ? "#0084D1" : hours > 0 ? "#E6F0FB" : "#F1F5F9",
            color: isToday && hours > 0 ? "#FFFFFF" : hours > 0 ? "#0084D1" : "#94A3B8",
        }}
    >
        {hours}h
    </span>
);

// 3. Botão de Anexo de Comprovativo (compact = versão reduzida para o cartão fechado)
const DocumentButton = ({
    hasDocument,
    onClick,
    outlined = false,
    compact = false,
}: {
    hasDocument: boolean;
    onClick: (e: React.MouseEvent) => void;
    outlined?: boolean;
    compact?: boolean;
}) => (
    <button
        type="button"
        onClick={onClick}
        title={hasDocument ? "Ver comprovativo" : "Anexar comprovativo"}
        className={`flex align-items-center gap-1 cursor-pointer py-1 flex-shrink-0 ${outlined ? "border-1 px-3 border-round-lg text-xs font-semibold" : "border-none px-2"
            }`}
        style={{
            borderRadius: "6px",
            backgroundColor: hasDocument ? "#E6F5ED" : "#E6F0FB",
            borderColor: hasDocument ? "#A7F3D0" : "#BAE6FD",
            color: hasDocument ? "#00853D" : "#0084D1",
            fontSize: compact ? "0.7rem" : "0.75rem",
            fontWeight: 600,
            height: compact ? "22px" : undefined,
        }}
    >
        <i className={hasDocument ? "pi pi-eye text-xs" : "pi pi-paperclip text-xs"} />
        <span>
            {compact
                ? hasDocument ? "Ver" : "Anexo"
                : hasDocument ? "Ver comprovativo" : "Anexar comprovativo"}
        </span>
    </button>
);

// COMPONENTE PRINCIPAL
const WeekDayCard: React.FC<WeekDayCardProps> = ({
    day,
    month,
    year,
    timesheetDay,
    extraHourPoIds,
    noHoursPoIds,
    documentRequired,
    hasDocument = false,
    onDocumentClick,
    isExpanded = false,
    onToggleExpand,
    onDeletePo,
    onDeleteAbscence,
    canEdit = false,
    isSuportService = false,
}) => {
    const date = new Date(year, month - 1, day);
    const dayOfWeekIndex = date.getDay();
    const weekday = WEEK_DAYS_SHORT[dayOfWeekIndex];
    const isWeekend = (dayOfWeekIndex === 0 || dayOfWeekIndex === 6) && !isSuportService;

    const today = new Date();
    const isToday =
        today.getDate() === day &&
        today.getMonth() === month - 1 &&
        today.getFullYear() === year;

    const dayNumberFormatted = String(day).padStart(2, "0");

    const poHours = timesheetDay?.purchaseOrders?.reduce((sum, po) => sum + (po.hours ?? 0), 0) ?? 0;
    const abscenceHours = timesheetDay?.abscenceTime ?? 0;
    const totalHours = poHours + abscenceHours;
    const abscence = timesheetDay?.abscenceType;

    // Deslocação/Prevenção have 0h but are still day entries 
    const purchaseOrders = useMemo(
        () =>
            timesheetDay?.purchaseOrders?.filter(
                (po) => (po.hours ?? 0) > 0 || (!!po.id && !!noHoursPoIds?.has(po.id))
            ) ?? [],
        [timesheetDay?.purchaseOrders, noHoursPoIds]
    );
    const hasContent = totalHours > 0 || purchaseOrders.length > 0;

    const hasAbscence = !!(abscence && abscenceHours > 0);

    // Igual à web (TimesheetDayForm): ausências diárias mostram-se como 1 dia,
    // embora sejam gravadas com as horas contratuais (workHoursPerDay)
    const isAbscenceInDays = abscenceTypesInDays.includes(abscence?.name ?? "");
    const abscenceAmountLabel = isAbscenceInDays ? "1d" : `${abscenceHours}h`;

    const allEntries = useMemo(() => {
        const entries: Array<{
            id: string;
            name: string;
            hours: number;
            amountLabel: string;
            type: "po" | "abscence";
            isExtra?: boolean;
        }> = [];

        purchaseOrders.forEach((po) => {
            entries.push({
                id: po.id ?? po.name,
                name: po.name,
                hours: po.hours ?? 0,
                amountLabel: `${po.hours ?? 0}h`,
                type: "po",
                isExtra: po.id ? extraHourPoIds?.has(po.id) : false,
            });
        });

        if (hasAbscence && abscence) {
            entries.push({
                id: abscence.id ?? "abscence",
                name: abscence.name,
                hours: abscenceHours,
                amountLabel: abscenceAmountLabel,
                type: "abscence",
            });
        }

        return entries;
    }, [purchaseOrders, hasAbscence, abscence, abscenceHours, abscenceAmountLabel, extraHourPoIds]);


    const { isPressing, handlers } = useLongPress({
        onLongPress: () => {
            if (hasContent) onToggleExpand?.();
        },
        onClick: () => {
            if (hasContent && !isExpanded) onToggleExpand?.();
        },
    });

    if (isExpanded && hasContent) {
        return (
            <div
                className="card-mobile p-3 flex flex-column gap-3 relative"
                style={{
                    backgroundColor: "#FFFFFF",
                    border: "1.5px solid #0084D1",
                    boxShadow: "0 4px 14px rgba(0, 132, 209, 0.08)",
                    borderRadius: "14px",
                }}
            >
                {/* Cabeçalho */}
                <div className="flex justify-content-between align-items-center">
                    <div className="flex align-items-center gap-2">
                        <DateSquircle day={dayNumberFormatted} weekday={weekday} isToday={isToday} isWeekend={isWeekend} />
                        <div className="flex flex-column">
                            <div className="flex align-items-center gap-2">
                                <span className="font-bold text-sm text-900">Entradas do dia</span>
                                <HoursBadge hours={totalHours} isToday={isToday} />
                            </div>
                            <span className="text-xs text-500 mt-1">Gere ou elimina os registos deste dia</span>
                        </div>
                    </div>
                    <i className="pi pi-times text-400 cursor-pointer p-2" onClick={onToggleExpand} style={{ fontSize: "0.9rem" }} />
                </div>

                {/* Lista de Registos */}
                <div className="flex flex-column gap-2">
                    {timesheetDay?.purchaseOrders?.map((po) => {
                        const poId = po.id;
                        const isExtra = poId ? extraHourPoIds?.has(poId) : false;
                        return (
                            <div
                                key={poId ?? po.name}
                                className="p-2 px-3 flex align-items-center justify-content-between"
                                style={{ backgroundColor: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "10px" }}
                            >
                                <div className="flex align-items-center gap-2 flex-1 min-w-0 pr-2">
                                    <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: isExtra ? "#7C3AED" : "#0084D1", flexShrink: 0 }} />
                                    <div className="flex flex-column min-w-0">
                                        <span className="font-semibold text-xs text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">{po.name}</span>
                                        <span className="text-xs text-500 mt-1">
                                            {poId && noHoursPoIds?.has(poId)
                                                ? "Sem contagem de horas"
                                                : `${po.hours ?? 0} horas ${isExtra ? "extras" : "normais"}`}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex align-items-center gap-2 flex-shrink-0">
                                    <span
                                        className="badge-squircle"
                                        style={{
                                            backgroundColor: isExtra ? "#F5F3FF" : "#E6F0FB",
                                            color: isExtra ? "#7C3AED" : "#0084D1",
                                            border: `1px solid ${isExtra ? "#DDD6FE" : "#D1E4FA"}`,
                                            padding: "3px 8px",
                                        }}
                                    >
                                        {po.hours ?? 0}h
                                    </span>
                                    {canEdit && poId && (
                                        <i
                                            className="pi pi-trash text-400 hover:text-red-500 cursor-pointer p-1"
                                            style={{ fontSize: "0.85rem" }}
                                            onClick={() => timesheetDay && onDeletePo?.(timesheetDay, poId)}
                                        />
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {abscence && abscenceHours > 0 && (
                        <div
                            className="p-2 px-3 flex align-items-center justify-content-between"
                            style={{ backgroundColor: "#FFFDF5", border: "1px solid #FEF3C7", borderRadius: "10px" }}
                        >
                            <div className="flex align-items-center gap-2 flex-1 min-w-0 pr-2">
                                <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#D97706", flexShrink: 0 }} />
                                <div className="flex flex-column min-w-0">
                                    <span className="font-semibold text-xs text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">{abscence.name}</span>
                                    <span className="text-xs mt-1" style={{ color: "#B45309" }}>{isAbscenceInDays ? "1 dia de ausência" : `${abscenceHours} horas de ausência`}</span>
                                </div>
                            </div>

                            <div className="flex align-items-center gap-2 flex-shrink-0">
                                <span className="badge-squircle" style={{ backgroundColor: "#FEF3C7", color: "#B45309", border: "1px solid #FDE68A", padding: "3px 8px" }}>
                                    {abscenceHours}h
                                </span>
                                {canEdit && !isVacationAbscence(abscence.name) && (
                                    <i
                                        className="pi pi-trash text-400 hover:text-red-500 cursor-pointer p-1"
                                        style={{ fontSize: "0.85rem" }}
                                        onClick={() => timesheetDay && onDeleteAbscence?.(timesheetDay)}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Rodapé */}
                <div className="flex justify-content-between align-items-center pt-1">
                    {documentRequired && abscence && timesheetDay ? (
                        <DocumentButton hasDocument={hasDocument} onClick={() => onDocumentClick?.(timesheetDay)} outlined />
                    ) : (
                        <div />
                    )}
                    <span className="text-xs font-semibold text-600 hover:text-900 cursor-pointer py-1 px-2" onClick={onToggleExpand}>
                        Fechar
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div
            {...(hasContent ? handlers : {})}
            className={`card-mobile p-3 flex align-items-center gap-3 relative ${hasContent ? "cursor-pointer touch-press-scale" : ""
                } ${isPressing ? "is-pressing" : ""}`}
            style={{
                backgroundColor: isWeekend && !hasContent ? "#FAFAFC" : "#FFFFFF",
                borderLeft: isToday ? "4px solid #0084D1" : "1px solid #E2E8F0",
                minHeight: "74px",
            }}
        >
            <DateSquircle day={dayNumberFormatted} weekday={weekday} isToday={isToday} isWeekend={isWeekend} />

            <div className="flex-1 min-w-0 flex flex-column justify-content-center gap-1">
                {allEntries.length > 0 && (
                    <div className="flex flex-column gap-1 min-w-0">
                        {allEntries.slice(0, 2).map((entry, index) => {
                            const showMore = index === 1 && allEntries.length > 2;
                            const showDocument = entry.type === "abscence" && documentRequired && !!timesheetDay;

                            return (
                                <div key={`${entry.type}-${entry.id}`} className="flex align-items-center gap-1 min-w-0">
                                    <MobileBadge
                                        size="sm"
                                        truncate
                                        tone={entry.type === "abscence" ? "abscence" : entry.isExtra ? "extra" : "primary"}
                                        label={`${entry.name}${entry.isExtra ? " (Hora Extra)" : ""}`}
                                        suffix={`(${entry.amountLabel})`}
                                        title={`${entry.name}${entry.isExtra ? " (Hora Extra)" : ""} (${entry.amountLabel})`}
                                    />

                                    {showDocument && (
                                        <DocumentButton
                                            compact
                                            hasDocument={hasDocument}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (timesheetDay) onDocumentClick?.(timesheetDay);
                                            }}
                                        />
                                    )}

                                    {showMore && (
                                        <MobileBadge
                                            size="sm"
                                            tone="neutral"
                                            className="font-bold"
                                            label={`+${allEntries.length - 2}`}
                                            title="Clique para ver todos os registos deste dia"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onToggleExpand?.();
                                            }}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}


                {!hasContent && !isWeekend && (
                    <span className="text-xs text-400" style={{ fontStyle: "italic" }}>
                        Sem registos submetidos
                    </span>
                )}
            </div>

            <div className="flex align-items-center justify-content-center flex-shrink-0">
                <HoursBadge hours={totalHours} isToday={isToday} />
            </div>
        </div>
    );

};
export default WeekDayCard;